import { z } from 'zod';

export * from './bank-import-lc';

// ============================================================================
// Proofline — Verified Trade Clearance
// ============================================================================

export const PaymentArrangementSchema = z.enum([
  'letter_of_credit',
  'open_account',
  'advance_tt',
  'partial_advance_balance',
  'documents_against_payment',
  'documents_against_acceptance',
  'buyer_led_supply_chain_finance',
  'factoring_receivables_finance',
  'consignment',
  'other',
]);
export type PaymentArrangement = z.infer<typeof PaymentArrangementSchema>;

export const TradeCaseStatusSchema = z.enum([
  'draft',
  'awaiting_payment',
  'submitted',
  'processing',
  'automated_review_complete',
  'awaiting_analyst_review',
  'action_required',
  'customer_resubmitted',
  'final_review',
  'cleared',
  'conditionally_cleared',
  'blocked',
  'cancelled',
  'closed',
]);
export type TradeCaseStatus = z.infer<typeof TradeCaseStatusSchema>;

export const ProoflineDecisionSchema = z.enum([
  'CLEAR',
  'CONDITIONAL_CLEARANCE',
  'ACTION_REQUIRED',
  'MANUAL_REVIEW_REQUIRED',
  'BLOCKED',
  'UNABLE_TO_ASSESS',
]);
export type ProoflineDecision = z.infer<typeof ProoflineDecisionSchema>;

export const ProoflineCheckStateSchema = z.enum([
  'pending',
  'running',
  'clear',
  'issue_found',
  'evidence_incomplete',
  'not_applicable',
  'unable_to_assess',
  'pending_review',
]);
export type ProoflineCheckState = z.infer<typeof ProoflineCheckStateSchema>;

export const ProoflineFindingStatusSchema = z.enum([
  'open',
  'acknowledged',
  'customer_action_required',
  'corrected',
  'accepted_exception',
  'false_positive',
  'resolved',
  'unable_to_resolve',
]);
export type ProoflineFindingStatus = z.infer<typeof ProoflineFindingStatusSchema>;

export const ProoflineActorTypeSchema = z.enum(['system', 'customer', 'reviewer']);
export type ProoflineActorType = z.infer<typeof ProoflineActorTypeSchema>;

export const ProoflineVisibilitySchema = z.enum(['customer', 'internal']);
export type ProoflineVisibility = z.infer<typeof ProoflineVisibilitySchema>;

export const ProoflineSeveritySchema = z.enum(['critical', 'high', 'medium', 'low', 'info']);
export type ProoflineSeverity = z.infer<typeof ProoflineSeveritySchema>;

export const ProoflineRuleReferenceSchema = z.object({
  id: z.string(),
  version: z.string().nullable().optional(),
  source: z.string(),
  article: z.string().nullable().optional(),
  url: z.string().url().nullable().optional(),
});
export type ProoflineRuleReference = z.infer<typeof ProoflineRuleReferenceSchema>;

export const ProoflineEvidenceReferenceSchema = z.object({
  document_id: z.string().uuid().nullable().optional(),
  document_version: z.number().int().positive().nullable().optional(),
  credential_reference: z.string().nullable().optional(),
  field: z.string().nullable().optional(),
  page: z.number().int().positive().nullable().optional(),
  location: z.string().nullable().optional(),
  hash: z.string().nullable().optional(),
});
export type ProoflineEvidenceReference = z.infer<typeof ProoflineEvidenceReferenceSchema>;

export const ProoflineFindingSchema = z.object({
  id: z.string().uuid(),
  source_module: z.string(),
  source_finding_id: z.string().nullable().optional(),
  category: z.string(),
  severity: ProoflineSeveritySchema,
  title: z.string(),
  explanation: z.string(),
  affected_entity: z.string().nullable().optional(),
  affected_document_id: z.string().uuid().nullable().optional(),
  affected_field: z.string().nullable().optional(),
  expected: z.string(),
  observed: z.string(),
  suggested_correction: z.string(),
  automated: z.boolean(),
  visibility: ProoflineVisibilitySchema,
  status: ProoflineFindingStatusSchema,
  reviewer_decision: z.string().nullable().optional(),
  rule_reference: ProoflineRuleReferenceSchema.nullable().optional(),
  evidence_references: z.array(ProoflineEvidenceReferenceSchema),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
});
export type ProoflineFinding = z.infer<typeof ProoflineFindingSchema>;

export const TradeCasePartySchema = z.object({
  id: z.string().uuid(),
  role: z.string(),
  name: z.string(),
  country_code: z.string().length(2).nullable().optional(),
  identifiers: z.record(z.unknown()).default({}),
});
export type TradeCaseParty = z.infer<typeof TradeCasePartySchema>;

export const TradeCaseDocumentSchema = z.object({
  id: z.string().uuid(),
  document_id: z.string().uuid(),
  logical_key: z.string(),
  document_type: z.string(),
  filename: z.string(),
  version: z.number().int().positive(),
  supersedes_id: z.string().uuid().nullable().optional(),
  correction_round: z.number().int().nonnegative(),
  is_current: z.boolean(),
  extraction_status: z.string().nullable().optional(),
  created_at: z.string().datetime(),
});
export type TradeCaseDocument = z.infer<typeof TradeCaseDocumentSchema>;

export const ProoflineCheckSchema = z.object({
  id: z.string().uuid(),
  module: z.string(),
  state: ProoflineCheckStateSchema,
  applicable: z.boolean(),
  applicability_reason: z.string(),
  source_record_type: z.string().nullable().optional(),
  source_record_id: z.string().nullable().optional(),
  summary: z.string().nullable().optional(),
  completed_at: z.string().datetime().nullable().optional(),
});
export type ProoflineCheck = z.infer<typeof ProoflineCheckSchema>;

export const ProoflineRemediationActionSchema = z.object({
  id: z.string().uuid(),
  finding_id: z.string().uuid(),
  requested_action: z.string(),
  responsible_party: z.string().nullable().optional(),
  requested_document_type: z.string().nullable().optional(),
  due_at: z.string().datetime().nullable().optional(),
  customer_response: z.string().nullable().optional(),
  correction_document_id: z.string().uuid().nullable().optional(),
  status: z.string(),
  correction_round: z.number().int().positive(),
});
export type ProoflineRemediationAction = z.infer<typeof ProoflineRemediationActionSchema>;

export const TradeCaseDecisionRecordSchema = z.object({
  id: z.string().uuid(),
  version: z.number().int().positive(),
  decision: ProoflineDecisionSchema,
  decision_type: z.enum(['recommendation', 'final']),
  summary: z.string(),
  reason: z.string(),
  reviewer_id: z.string().uuid().nullable().optional(),
  decided_at: z.string().datetime(),
  report_version: z.number().int().positive().nullable().optional(),
});
export type TradeCaseDecisionRecord = z.infer<typeof TradeCaseDecisionRecordSchema>;

export const ProoflineServicePackageSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string(),
  price_label: z.string(),
  currency: z.string().length(3),
  amount_cents: z.number().int().nonnegative().nullable().optional(),
  included_documents: z.number().int().nonnegative().nullable().optional(),
  included_parties: z.number().int().nonnegative().nullable().optional(),
  included_correction_rounds: z.number().int().nonnegative(),
  turnaround_class: z.string().nullable().optional(),
  features: z.array(z.string()),
  billing_mode: z.string(),
  self_service_enabled: z.boolean(),
});
export type ProoflineServicePackage = z.infer<typeof ProoflineServicePackageSchema>;

export const ProoflineQuoteSchema = z.object({
  package: ProoflineServicePackageSchema,
  currency: z.string().length(3),
  base_amount_cents: z.number().int().nonnegative(),
  credit_amount_cents: z.number().int().nonnegative().default(0),
  amount_due_cents: z.number().int().nonnegative(),
  credit_eligible_until: z.string().datetime().nullable().optional(),
  checkout_enabled: z.boolean(),
});
export type ProoflineQuote = z.infer<typeof ProoflineQuoteSchema>;

export const ProoflineReportAccessSchema = z.object({
  report_id: z.string().uuid(),
  report_version: z.number().int().positive(),
  final_decision: ProoflineDecisionSchema,
  generated_at: z.string().datetime(),
  download_url: z.string().url(),
  content_type: z.string(),
  expires_in_seconds: z.number().int().positive(),
});
export type ProoflineReportAccess = z.infer<typeof ProoflineReportAccessSchema>;

export const TradeCaseOutcomeSchema = z.object({
  id: z.string().uuid(),
  trade_case_id: z.string().uuid(),
  documents_accepted: z.boolean().nullable().optional(),
  payment_delayed: z.boolean().nullable().optional(),
  bank_additional_discrepancies: z.boolean().nullable().optional(),
  shipment_held: z.boolean().nullable().optional(),
  notes: z.string().nullable().optional(),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
});
export type TradeCaseOutcome = z.infer<typeof TradeCaseOutcomeSchema>;

export const TradeCaseSummarySchema = z.object({
  id: z.string().uuid(),
  case_reference: z.string(),
  company_id: z.string().uuid(),
  title: z.string(),
  status: TradeCaseStatusSchema,
  payment_arrangement: PaymentArrangementSchema,
  service_package_id: z.string().nullable().optional(),
  payment_status: z.string().nullable().optional(),
  amount_paid_cents: z.number().int().nonnegative().nullable().optional(),
  credit_amount_cents: z.number().int().nonnegative(),
  payment_currency: z.string().length(3).nullable().optional(),
  recommended_decision: ProoflineDecisionSchema.nullable(),
  final_decision: ProoflineDecisionSchema.nullable(),
  currency: z.string().nullable().optional(),
  amount: z.string().nullable().optional(),
  origin_country: z.string().length(2).nullable().optional(),
  destination_country: z.string().length(2).nullable().optional(),
  document_count: z.number().int().nonnegative(),
  finding_counts: z.record(z.number().int().nonnegative()),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
});
export type TradeCaseSummary = z.infer<typeof TradeCaseSummarySchema>;

export const TradeCaseDetailSchema = TradeCaseSummarySchema.extend({
  customer_user_id: z.string().uuid().nullable().optional(),
  owner_user_id: z.string().uuid().nullable().optional(),
  payment_terms: z.string().nullable().optional(),
  shipment_date: z.string().date().nullable().optional(),
  expected_payment_date: z.string().date().nullable().optional(),
  transaction_details: z.record(z.unknown()).default({}),
  source_lcopilot_session_id: z.string().uuid().nullable().optional(),
  final_report_id: z.string().uuid().nullable().optional(),
  parties: z.array(TradeCasePartySchema),
  documents: z.array(TradeCaseDocumentSchema),
  checks: z.array(ProoflineCheckSchema),
  findings: z.array(ProoflineFindingSchema),
  actions: z.array(ProoflineRemediationActionSchema),
  decision_history: z.array(TradeCaseDecisionRecordSchema),
});
export type TradeCaseDetail = z.infer<typeof TradeCaseDetailSchema>;

// ============================================================================
// Health Check Types
// ============================================================================

export const ServiceStatusSchema = z.enum(['connected', 'disconnected']);
export type ServiceStatus = z.infer<typeof ServiceStatusSchema>;

export const HealthStatusSchema = z.enum(['healthy', 'unhealthy']);
export type HealthStatus = z.infer<typeof HealthStatusSchema>;

export const HealthResponseSchema = z.object({
  status: HealthStatusSchema,
  timestamp: z.string().datetime(),
  version: z.string(),
  services: z.object({
    database: ServiceStatusSchema,
    redis: ServiceStatusSchema.optional(),
  }),
});
export type HealthResponse = z.infer<typeof HealthResponseSchema>;

// ============================================================================
// Error Response Types
// ============================================================================

export const ApiErrorSchema = z.object({
  error: z.string(),
  message: z.string(),
  details: z.record(z.unknown()).optional(),
  timestamp: z.string().datetime(),
  path: z.string().optional(),
  method: z.string().optional(),
});
export type ApiError = z.infer<typeof ApiErrorSchema>;

export const ValidationErrorSchema = z.object({
  error: z.literal('validation_error'),
  message: z.string(),
  details: z.object({
    field_errors: z.array(z.object({
      field: z.string(),
      message: z.string(),
      code: z.string(),
    })),
  }),
  timestamp: z.string().datetime(),
});
export type ValidationError = z.infer<typeof ValidationErrorSchema>;

// ============================================================================
// Authentication Types
// ============================================================================

export const UserRoleSchema = z.enum(['admin', 'user', 'viewer']);
export type UserRole = z.infer<typeof UserRoleSchema>;

export const AuthTokenSchema = z.object({
  access_token: z.string(),
  refresh_token: z.string(),
  token_type: z.literal('bearer'),
  expires_in: z.number(),
});
export type AuthToken = z.infer<typeof AuthTokenSchema>;

export const UserProfileSchema = z.object({
  id: z.string().uuid(),
  email: z.string().email(),
  name: z.string(),
  role: UserRoleSchema,
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
});
export type UserProfile = z.infer<typeof UserProfileSchema>;

// ============================================================================
// File Upload Types
// ============================================================================

export const FileUploadStatusSchema = z.enum([
  'pending',
  'uploading',
  'processing',
  'completed',
  'failed',
]);
export type FileUploadStatus = z.infer<typeof FileUploadStatusSchema>;

export const FileUploadRequestSchema = z.object({
  filename: z.string(),
  content_type: z.string(),
  size: z.number().positive(),
});
export type FileUploadRequest = z.infer<typeof FileUploadRequestSchema>;

export const FileUploadResponseSchema = z.object({
  upload_id: z.string().uuid(),
  upload_url: z.string().url(),
  fields: z.record(z.string()),
  expires_at: z.string().datetime(),
});
export type FileUploadResponse = z.infer<typeof FileUploadResponseSchema>;

export const FileInfoSchema = z.object({
  id: z.string().uuid(),
  filename: z.string(),
  content_type: z.string(),
  size: z.number(),
  status: FileUploadStatusSchema,
  upload_url: z.string().url().optional(),
  download_url: z.string().url().optional(),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
});
export type FileInfo = z.infer<typeof FileInfoSchema>;

// ============================================================================
// OCR Processing Types
// ============================================================================

export const OcrJobStatusSchema = z.enum([
  'queued',
  'processing',
  'completed',
  'failed',
  'cancelled',
]);
export type OcrJobStatus = z.infer<typeof OcrJobStatusSchema>;

export const OcrJobRequestSchema = z.object({
  file_id: z.string().uuid(),
  language: z.string().default('eng+ben'), // English + Bengali
  options: z.object({
    deskew: z.boolean().default(true),
    remove_background: z.boolean().default(false),
    enhance_contrast: z.boolean().default(true),
  }).optional(),
});
export type OcrJobRequest = z.infer<typeof OcrJobRequestSchema>;

export const OcrResultSchema = z.object({
  text: z.string(),
  confidence: z.number().min(0).max(100),
  language_detected: z.string(),
  processing_time_ms: z.number(),
  word_count: z.number(),
  character_count: z.number(),
});
export type OcrResult = z.infer<typeof OcrResultSchema>;

export const OcrJobResponseSchema = z.object({
  job_id: z.string().uuid(),
  file_id: z.string().uuid(),
  status: OcrJobStatusSchema,
  result: OcrResultSchema.optional(),
  error_message: z.string().optional(),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
  completed_at: z.string().datetime().optional(),
});
export type OcrJobResponse = z.infer<typeof OcrJobResponseSchema>;

// ============================================================================
// Report Generation Types
// ============================================================================

export const ReportFormatSchema = z.enum(['pdf', 'docx', 'html']);
export type ReportFormat = z.infer<typeof ReportFormatSchema>;

export const ReportTemplateSchema = z.enum(['standard', 'detailed', 'summary']);
export type ReportTemplate = z.infer<typeof ReportTemplateSchema>;

export const ReportRequestSchema = z.object({
  ocr_job_ids: z.array(z.string().uuid()),
  format: ReportFormatSchema,
  template: ReportTemplateSchema,
  options: z.object({
    include_original_images: z.boolean().default(false),
    include_confidence_scores: z.boolean().default(true),
    language: z.string().default('en'),
  }).optional(),
});
export type ReportRequest = z.infer<typeof ReportRequestSchema>;

export const ReportJobSchema = z.object({
  job_id: z.string().uuid(),
  status: z.enum(['queued', 'generating', 'completed', 'failed']),
  download_url: z.string().url().optional(),
  expires_at: z.string().datetime().optional(),
  error_message: z.string().optional(),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
});
export type ReportJob = z.infer<typeof ReportJobSchema>;

// ============================================================================
// Pagination Types
// ============================================================================

export const SeverityBreakdownSchema = z.object({
  critical: z.number().nonnegative(),
  major: z.number().nonnegative(),
  medium: z.number().nonnegative(),
  minor: z.number().nonnegative(),
});
export type SeverityBreakdown = z.infer<typeof SeverityBreakdownSchema>;

export const ProcessingSummarySchema = z.object({
  total_documents: z.number().nonnegative(),
  successful_extractions: z.number().nonnegative(),
  failed_extractions: z.number().nonnegative(),
  total_issues: z.number().nonnegative(),
  severity_breakdown: SeverityBreakdownSchema,
  processing_time_seconds: z.number().nullable().optional(),
  processing_time_display: z.string().nullable().optional(),
  processing_time_ms: z.number().nullable().optional(),
  processing_time_scope: z.enum(['validation_only', 'extraction_and_validation']).nullable().optional(),
  extraction_time_seconds: z.number().nullable().optional(),
  validation_time_seconds: z.number().nullable().optional(),
}).passthrough();
export type ProcessingSummary = z.infer<typeof ProcessingSummarySchema>;

export const ProcessingSummaryV2Schema = z.object({
  version: z.literal('processing_summary_v2'),
  total_documents: z.number().nonnegative(),
  documents: z.number().nonnegative().optional(),
  documents_found: z.number().nonnegative().optional(),
  verified: z.number().nonnegative(),
  warnings: z.number().nonnegative(),
  errors: z.number().nonnegative(),
  successful_extractions: z.number().nonnegative(),
  failed_extractions: z.number().nonnegative(),
  total_issues: z.number().nonnegative(),
  discrepancies: z.number().nonnegative().optional(),
  severity_breakdown: SeverityBreakdownSchema,
  status_counts: z.object({
    success: z.number().nonnegative(),
    warning: z.number().nonnegative(),
    error: z.number().nonnegative(),
  }),
  document_status: z.object({
    success: z.number().nonnegative(),
    warning: z.number().nonnegative(),
    error: z.number().nonnegative(),
  }).optional(),
  compliance_rate: z.number().min(0).max(100),
  processing_time_seconds: z.number().nullable().optional(),
  processing_time_display: z.string().nullable().optional(),
  processing_time_ms: z.number().nullable().optional(),
  processing_time_scope: z.enum(['validation_only', 'extraction_and_validation']).nullable().optional(),
  extraction_time_seconds: z.number().nullable().optional(),
  validation_time_seconds: z.number().nullable().optional(),
  extraction_quality: z.number().nullable().optional(),
}).passthrough();
export type ProcessingSummaryV2 = z.infer<typeof ProcessingSummaryV2Schema>;

export const ExtractionFieldEvidenceSchema = z.object({
  source: z.string(),
  snippet: z.string().nullable().optional(),
  strategy: z.string().optional(),
}).passthrough();
export type ExtractionFieldEvidence = z.infer<typeof ExtractionFieldEvidenceSchema>;

export const ExtractionFieldDetailSchema = z.object({
  value: z.unknown().optional(),
  raw_value: z.unknown().optional(),
  confidence: z.number().min(0).max(1).optional(),
  status: z.string().optional(),
  validator_agrees: z.boolean().optional(),
  issues: z.array(z.string()).optional(),
  source: z.string().optional(),
  verification: z.string().optional(),
  evidence: ExtractionFieldEvidenceSchema.nullable().optional(),
}).passthrough();
export type ExtractionFieldDetail = z.infer<typeof ExtractionFieldDetailSchema>;

export const DocumentFactSchema = z.object({
  field_name: z.string(),
  value: z.unknown().optional(),
  normalized_value: z.unknown().optional(),
  confidence: z.number().min(0).max(1).optional(),
  verification_state: z.string().default('unconfirmed'),
  origin: z.string().default('unknown'),
  source_field_name: z.string().nullable().optional(),
  evidence_snippet: z.string().nullable().optional(),
  evidence_source: z.string().nullable().optional(),
  page: z.number().int().positive().nullable().optional(),
}).passthrough();
export type DocumentFact = z.infer<typeof DocumentFactSchema>;

export const DocumentFactSetSchema = z.object({
  version: z.literal('fact_graph_v1').default('fact_graph_v1'),
  document_type: z.string(),
  document_subtype: z.string().nullable().optional(),
  facts: z.array(DocumentFactSchema).default([]),
}).passthrough();
export type DocumentFactSet = z.infer<typeof DocumentFactSetSchema>;

export const RequirementsGraphDocumentSchema = z.object({
  code: z.string(),
  display_name: z.string().optional(),
  category: z.string().optional(),
  raw_text: z.string().optional(),
  aliases_matched: z.array(z.string()).optional(),
  originals: z.number().int().nullable().optional(),
  copies: z.number().int().nullable().optional(),
  signed: z.boolean().nullable().optional(),
  negotiable: z.boolean().nullable().optional(),
  issuer: z.string().nullable().optional(),
  exact_wording: z.string().nullable().optional(),
  legalized: z.boolean().nullable().optional(),
  transport_mode: z.string().nullable().optional(),
  detection_source: z.string().optional(),
  confidence: z.number().optional(),
  evidence: z.array(z.string()).optional(),
}).passthrough();
export type RequirementsGraphDocument = z.infer<typeof RequirementsGraphDocumentSchema>;

export const RequirementsGraphConditionRequirementSchema = z.object({
  requirement_type: z.string(),
  identifier_type: z.string().nullable().optional(),
  value: z.string().nullable().optional(),
  applies_to: z.string().nullable().optional(),
  document_type: z.string().nullable().optional(),
  field_name: z.string().nullable().optional(),
  originals_required: z.number().int().nullable().optional(),
  copies_required: z.number().int().nullable().optional(),
  exact_wording: z.string().nullable().optional(),
  source_text: z.string().nullable().optional(),
  source_bucket: z.string().nullable().optional(),
}).passthrough();
export type RequirementsGraphConditionRequirement = z.infer<typeof RequirementsGraphConditionRequirementSchema>;

export const RequirementsGraphV1Schema = z.object({
  version: z.literal('requirements_graph_v1').default('requirements_graph_v1'),
  source_document_id: z.string().nullable().optional(),
  source_document_type: z.string().nullable().optional(),
  source_lane: z.string().nullable().optional(),
  workflow_orientation: z.string().nullable().optional(),
  applicable_rules: z.string().nullable().optional(),
  required_documents: z.array(RequirementsGraphDocumentSchema).default([]),
  required_document_types: z.array(z.string()).default([]),
  documentary_conditions: z.array(z.string()).default([]),
  non_documentary_conditions: z.array(z.string()).default([]),
  ambiguous_conditions: z.array(z.string()).default([]),
  condition_requirements: z.array(RequirementsGraphConditionRequirementSchema).default([]),
  required_fact_fields: z.array(z.string()).default([]),
  core_terms: z.record(z.unknown()).default({}),
}).passthrough();
export type RequirementsGraphV1 = z.infer<typeof RequirementsGraphV1Schema>;

export const ResolutionQueueItemSchema = z.object({
  document_id: z.string(),
  document_type: z.string(),
  filename: z.string().nullable().optional(),
  field_name: z.string(),
  label: z.string(),
  priority: z.string(),
  candidate_value: z.unknown().optional(),
  normalized_value: z.unknown().optional(),
  evidence_snippet: z.string().nullable().optional(),
  evidence_source: z.string().nullable().optional(),
  page: z.number().int().positive().nullable().optional(),
  reason: z.string(),
  verification_state: z.string(),
  resolvable_by_user: z.boolean(),
  origin: z.string().nullable().optional(),
}).passthrough();
export type ResolutionQueueItem = z.infer<typeof ResolutionQueueItemSchema>;

export const ResolutionQueueSummarySchema = z.object({
  total_items: z.number().nonnegative(),
  user_resolvable_items: z.number().nonnegative(),
  unresolved_documents: z.number().nonnegative(),
  document_counts: z.record(z.number()).default({}),
}).passthrough();
export type ResolutionQueueSummary = z.infer<typeof ResolutionQueueSummarySchema>;

export const ResolutionQueueV1Schema = z.object({
  version: z.literal('resolution_queue_v1').default('resolution_queue_v1'),
  items: z.array(ResolutionQueueItemSchema).default([]),
  summary: ResolutionQueueSummarySchema,
}).passthrough();
export type ResolutionQueueV1 = z.infer<typeof ResolutionQueueV1Schema>;

export const FactResolutionDocumentSchema = z.object({
  document_id: z.string(),
  document_type: z.string(),
  filename: z.string().nullable().optional(),
  resolution_required: z.boolean(),
  ready_for_validation: z.boolean(),
  unresolved_count: z.number().nonnegative(),
  summary: z.string(),
  fact_graph_v1: DocumentFactSetSchema.optional(),
  requirements_graph_v1: RequirementsGraphV1Schema.optional(),
  resolution_items: z.array(ResolutionQueueItemSchema).default([]),
}).passthrough();
export type FactResolutionDocument = z.infer<typeof FactResolutionDocumentSchema>;

export const FactResolutionSummarySchema = z.object({
  total_documents: z.number().nonnegative(),
  unresolved_documents: z.number().nonnegative(),
  total_items: z.number().nonnegative(),
  user_resolvable_items: z.number().nonnegative(),
  ready_for_validation: z.boolean(),
}).passthrough();
export type FactResolutionSummary = z.infer<typeof FactResolutionSummarySchema>;

export const ExtractionResolutionFieldSchema = z.object({
  field_name: z.string(),
  label: z.string(),
  verification: z.string().optional(),
  reason_code: z.string().optional().nullable(),
}).passthrough();
export type ExtractionResolutionField = z.infer<typeof ExtractionResolutionFieldSchema>;

export const ExtractionResolutionSchema = z.object({
  required: z.boolean(),
  unresolved_count: z.number().nonnegative(),
  summary: z.string(),
  fields: z.array(ExtractionResolutionFieldSchema).optional(),
  source: z.string().optional(),
}).passthrough();
export type ExtractionResolution = z.infer<typeof ExtractionResolutionSchema>;

export const WorkflowStageInfoSchema = z.object({
  stage: z.enum(['upload', 'extraction_resolution', 'validation_results']),
  provisional_validation: z.boolean(),
  ready_for_final_validation: z.boolean(),
  unresolved_documents: z.number().nonnegative(),
  unresolved_fields: z.number().nonnegative(),
  document_lane_counts: z.record(z.number()).optional(),
  summary: z.string(),
}).passthrough();
export type WorkflowStageInfo = z.infer<typeof WorkflowStageInfoSchema>;

export const FactResolutionV1Schema = z.object({
  version: z.literal('fact_resolution_v1').default('fact_resolution_v1'),
  workflow_stage: WorkflowStageInfoSchema.optional(),
  documents: z.array(FactResolutionDocumentSchema).default([]),
  summary: FactResolutionSummarySchema,
}).passthrough();
export type FactResolutionV1 = z.infer<typeof FactResolutionV1Schema>;

export const FieldOverrideRequestSchema = z.object({
  document_id: z.string(),
  field_name: z.string(),
  override_value: z.unknown(),
  verification: z.enum(['operator_confirmed', 'operator_rejected']).optional(),
  note: z.string().trim().max(1000).optional(),
});
export type FieldOverrideRequest = z.infer<typeof FieldOverrideRequestSchema>;

export const DocumentExtractionDocumentSchema = z.object({
  document_id: z.string().optional(),
  document_type: z.string().optional(),
  filename: z.string().optional(),
  status: z.enum(['success', 'warning', 'error']).optional(),
  extraction_status: z.string().optional(),
  extraction_lane: z.string().optional(),
  extractionLane: z.string().optional(),
  extracted_fields: z.record(z.unknown()).optional(),
  field_details: z.record(ExtractionFieldDetailSchema).optional(),
  fieldDetails: z.record(ExtractionFieldDetailSchema).optional(),
  fact_graph_v1: DocumentFactSetSchema.optional(),
  factGraphV1: DocumentFactSetSchema.optional(),
  requirements_graph_v1: RequirementsGraphV1Schema.optional(),
  requirementsGraphV1: RequirementsGraphV1Schema.optional(),
  extraction_resolution: ExtractionResolutionSchema.optional(),
  extractionResolution: ExtractionResolutionSchema.optional(),
  issues_count: z.number().nonnegative().optional(),
  ocr_confidence: z.number().optional(),
  review_required: z.boolean().optional(),
  review_reasons: z.array(z.string()).optional(),
  critical_field_states: z.record(z.string()).optional(),
  extraction_debug: z.record(z.unknown()).optional(),
}).passthrough();
export type DocumentExtractionDocument = z.infer<typeof DocumentExtractionDocumentSchema>;

export const DocumentExtractionSummarySchema = z.object({
  total_documents: z.number().nonnegative(),
  status_counts: z.object({
    success: z.number().nonnegative(),
    warning: z.number().nonnegative(),
    error: z.number().nonnegative(),
  }),
}).passthrough();
export type DocumentExtractionSummary = z.infer<typeof DocumentExtractionSummarySchema>;

export const DocumentExtractionV1Schema = z.object({
  version: z.literal('document_extraction_v1'),
  documents: z.array(DocumentExtractionDocumentSchema),
  summary: DocumentExtractionSummarySchema,
}).passthrough();
export type DocumentExtractionV1 = z.infer<typeof DocumentExtractionV1Schema>;

export const IssueProvenanceEntrySchema = z.object({
  issue_id: z.string(),
  source: z.string(),
  ruleset_domain: z.string().optional(),
  rule: z.string().optional(),
  severity: z.string().optional(),
  document_ids: z.union([z.array(z.string()), z.string()]).optional(),
  document_types: z.union([z.array(z.string()), z.string()]).optional(),
  document_names: z.union([z.array(z.string()), z.string()]).optional(),
}).passthrough();
export type IssueProvenanceEntry = z.infer<typeof IssueProvenanceEntrySchema>;

export const IssueProvenanceV1Schema = z.object({
  version: z.literal('issue_provenance_v1'),
  issues: z.array(IssueProvenanceEntrySchema),
}).passthrough();
export type IssueProvenanceV1 = z.infer<typeof IssueProvenanceV1Schema>;

export const StructuredResultDocumentSchema = z.object({
  document_id: z.string().optional(),
  document_type: z.string().optional(),
  filename: z.string().optional(),
  extraction_status: z.string().optional(),
  extraction_lane: z.string().optional(),
  extractionLane: z.string().optional(),
  extracted_fields: z.record(z.unknown()).optional(),
  field_details: z.record(ExtractionFieldDetailSchema).optional(),
  fieldDetails: z.record(ExtractionFieldDetailSchema).optional(),
  fact_graph_v1: DocumentFactSetSchema.optional(),
  factGraphV1: DocumentFactSetSchema.optional(),
  requirements_graph_v1: RequirementsGraphV1Schema.optional(),
  requirementsGraphV1: RequirementsGraphV1Schema.optional(),
  extraction_resolution: ExtractionResolutionSchema.optional(),
  extractionResolution: ExtractionResolutionSchema.optional(),
  issues_count: z.number().nonnegative().optional(),
  review_required: z.boolean().optional(),
  review_reasons: z.array(z.string()).optional(),
  critical_field_states: z.record(z.string()).optional(),
  extraction_debug: z.record(z.unknown()).optional(),
  day1_runtime: z.record(z.unknown()).optional(),
  // Legacy camelCase fields for backward compatibility
  id: z.string().optional(),
  name: z.string().optional(),
  type: z.string().optional(),
  extractionStatus: z.string().optional(),
  extractedFields: z.record(z.unknown()).optional(),
  discrepancy_count: z.number().nonnegative().optional(),
  discrepancyCount: z.number().nonnegative().optional(),
  issues: z.number().nonnegative().optional(),
}).passthrough();
export type StructuredResultDocument = z.infer<typeof StructuredResultDocumentSchema>;

export const FieldOverrideResponseSchema = z.object({
  job_id: z.string(),
  jobId: z.string(),
  document_id: z.string(),
  field_name: z.string(),
  override_value: z.unknown(),
  verification: z.enum(['operator_confirmed', 'operator_rejected']),
  applied_at: z.string().datetime(),
  updated_document: StructuredResultDocumentSchema.optional(),
}).passthrough();
export type FieldOverrideResponse = z.infer<typeof FieldOverrideResponseSchema>;

export const StructuredResultIssueSchema = z.object({
  id: z.string(),
  title: z.string(),
  severity: z.string(),
  priority: z.string().optional(),
  documents: z.array(z.string()),
  expected: z.string(),
  found: z.string(),
  suggested_fix: z.string(),
  description: z.string().optional(),
  reference: z.string().optional().nullable(),
  ucp_reference: z.string().optional().nullable(),
  lc_clause: z.string().optional(),
  impact: z.string().optional(),
  source_layer: z.string().optional(),
  field_name: z.string().optional(),
  source_field: z.string().optional(),
  target_field: z.string().optional(),
  dependency_fields: z.array(z.string()).optional(),
  provisional: z.boolean().optional(),
  provisional_reason: z.string().optional(),
  extraction_confidence: z.number().optional(),
  requirement_source: z.string().optional(),
  requirement_kind: z.string().optional(),
  requirement_text: z.string().optional(),
});
export type StructuredResultIssue = z.infer<typeof StructuredResultIssueSchema>;

export const DocumentRiskEntrySchema = z.object({
  document_id: z.string().optional(),
  filename: z.string().optional(),
  risk: z.string().default('low'),
});
export type DocumentRiskEntry = z.infer<typeof DocumentRiskEntrySchema>;

export const StructuredResultAnalyticsSchema = z.object({
  compliance_score: z.number(),
  issue_counts: SeverityBreakdownSchema,
  document_risk: z.array(DocumentRiskEntrySchema),
  lc_compliance_score: z.number().optional(),
  document_status_distribution: z.object({
    success: z.number().optional(),
    warning: z.number().optional(),
    error: z.number().optional(),
  }).optional(),
  documents_processed: z.number().optional(),
  processing_time_display: z.string().optional().nullable(),
  compliance_level: z.string().optional(),
  compliance_cap_reason: z.string().optional().nullable(),
  customs_risk: z.object({
    score: z.number(),
    tier: z.string(),
    flags: z.array(z.string()).optional(),
  }).optional(),
});
export type StructuredResultAnalytics = z.infer<typeof StructuredResultAnalyticsSchema>;

export const StructuredTimelineEntrySchema = z.object({
  title: z.string().optional(),
  status: z.string(),
  description: z.string().optional(),
  timestamp: z.string().optional(),
  label: z.string().optional(),
});
export type StructuredTimelineEntry = z.infer<typeof StructuredTimelineEntrySchema>;
export const TimelineEntrySchema = StructuredTimelineEntrySchema;
export type TimelineEntry = StructuredTimelineEntry;

export const AiValidationLayerSchema = z.object({
  layer: z.enum(['L1', 'L2', 'L3']),
  label: z.string(),
  executed: z.boolean(),
  verdict: z.enum(['pass', 'warn', 'reject', 'not_run']),
  issue_count: z.number().nonnegative(),
  critical_issues: z.number().nonnegative(),
  major_issues: z.number().nonnegative(),
  minor_issues: z.number().nonnegative(),
  checks_performed: z.array(z.string()),
  reason: z.string().optional(),
  evidence: z.record(z.unknown()).optional(),
}).passthrough();
export type AiValidationLayer = z.infer<typeof AiValidationLayerSchema>;

export const AiValidationLayersSchema = z.object({
  l1: AiValidationLayerSchema.optional(),
  l2: AiValidationLayerSchema.optional(),
  l3: AiValidationLayerSchema.optional(),
}).passthrough();
export type AiValidationLayers = z.infer<typeof AiValidationLayersSchema>;

export const AiValidationSummarySchema = z.object({
  issue_count: z.number().nonnegative().optional(),
  critical_issues: z.number().nonnegative().optional(),
  major_issues: z.number().nonnegative().optional(),
  minor_issues: z.number().nonnegative().optional(),
  documents_checked: z.number().nonnegative().optional(),
  derived_ai_verdict: z.enum(['pass', 'warn', 'reject']).optional(),
  layer_contract_version: z.string().optional(),
  execution_position: z.string().optional(),
  layers: AiValidationLayersSchema.optional(),
  metadata: z.record(z.unknown()).optional(),
  timed_out: z.boolean().optional(),
}).passthrough();
export type AiValidationSummary = z.infer<typeof AiValidationSummarySchema>;

export const ValidationContractV1Schema = z.object({
  ai_verdict: z.string().optional(),
  ai_layers: AiValidationLayersSchema.optional().nullable(),
  ai_execution_position: z.string().optional(),
  ai_layer_contract_version: z.string().optional(),
  ruleset_verdict: z.string().optional(),
  final_verdict: z.enum(['pass', 'review', 'reject']).optional(),
  arbitration_mode: z.string().optional(),
  next_action: z.string().optional(),
  review_required_reason: z.array(z.string()).optional(),
  escalation_triggers: z.array(z.string()).optional(),
  rules_evidence: z.record(z.unknown()).optional(),
  evidence_summary: z.record(z.unknown()).optional(),
}).passthrough();
export type ValidationContractV1 = z.infer<typeof ValidationContractV1Schema>;

export const SubmissionEligibilitySchema = z.object({
  can_submit: z.boolean().optional(),
  reasons: z.array(z.string()).optional(),
  source: z.string().optional(),
  missing_reason_codes: z.array(z.string()).optional(),
  unresolved_critical_fields: z.array(z.union([z.string(), z.record(z.unknown())])).optional(),
}).passthrough();
export type SubmissionEligibility = z.infer<typeof SubmissionEligibilitySchema>;

export const BankVerdictActionItemSchema = z.object({
  priority: z.string().optional(),
  issue: z.string().optional(),
  action: z.string().optional(),
}).passthrough();
export type BankVerdictActionItem = z.infer<typeof BankVerdictActionItemSchema>;

export const BankVerdictSchema = z.object({
  verdict: z.string().optional(),
  verdict_color: z.string().optional(),
  verdict_message: z.string().optional(),
  recommendation: z.string().optional(),
  can_submit: z.boolean().optional(),
  will_be_rejected: z.boolean().optional(),
  estimated_discrepancy_fee: z.number().optional(),
  issue_summary: z.object({
    critical: z.number().optional(),
    major: z.number().optional(),
    minor: z.number().optional(),
    total: z.number().optional(),
  }).passthrough().optional(),
  action_items: z.array(BankVerdictActionItemSchema).optional(),
  action_items_count: z.number().optional(),
  reasons: z.array(z.string()).optional(),
  risk_flags: z.array(z.string()).optional(),
}).passthrough();
export type BankVerdict = z.infer<typeof BankVerdictSchema>;

export const BankProfileSchema = z.object({
  bank_code: z.string(),
  bank_name: z.string(),
  strictness: z.enum(['lenient', 'standard', 'strict']),
  country: z.string().optional(),
  region: z.string().optional(),
  document_preferences: z.array(z.string()).optional(),
  special_requirements: z.array(z.string()).optional(),
  blocked_conditions: z.array(z.string()).optional(),
  tolerance_level: z.number().optional(),
  port_rules: z.record(z.unknown()).optional(),
  date_rules: z.record(z.unknown()).optional(),
  amount_rules: z.record(z.unknown()).optional(),
  party_rules: z.record(z.unknown()).optional(),
}).passthrough();
export type BankProfile = z.infer<typeof BankProfileSchema>;

export const AmendmentFieldChangeSchema = z.object({
  tag: z.string(),
  name: z.string(),
  current: z.string(),
  proposed: z.string(),
});
export type AmendmentFieldChange = z.infer<typeof AmendmentFieldChangeSchema>;

export const AmendmentSchema = z.object({
  issue_id: z.string(),
  field: AmendmentFieldChangeSchema,
  narrative: z.string(),
  swift_mt707_text: z.string().optional(),
  mt707_text: z.string().optional(),
  iso20022_xml: z.string().optional(),
  bank_processing_days: z.number().optional(),
  estimated_fee_usd: z.number().optional(),
}).passthrough();
export type Amendment = z.infer<typeof AmendmentSchema>;

export const AmendmentsAvailableSchema = z.object({
  count: z.number(),
  amendments: z.array(AmendmentSchema),
  total_estimated_fee_usd: z.number().optional(),
  total_processing_days: z.number().optional(),
}).passthrough();
export type AmendmentsAvailable = z.infer<typeof AmendmentsAvailableSchema>;

export const LcRequiredDocumentSchema = z.object({
  code: z.string(),
  display_name: z.string(),
  category: z.string(),
  raw_text: z.string(),
  aliases_matched: z.array(z.string()),
  originals: z.number().nullable().optional(),
  copies: z.number().nullable().optional(),
  signed: z.boolean().nullable().optional(),
  negotiable: z.boolean().nullable().optional(),
  issuer: z.string().nullable().optional(),
  exact_wording: z.string().nullable().optional(),
  legalized: z.boolean().nullable().optional(),
  transport_mode: z.string().nullable().optional(),
  detection_source: z.string(),
  confidence: z.number(),
  evidence: z.array(z.string()),
}).passthrough();
export type LcRequiredDocument = z.infer<typeof LcRequiredDocumentSchema>;

export const LcClassificationAttributesSchema = z.object({
  revocability: z.string(),
  availability: z.string(),
  available_with_scope: z.string(),
  confirmation: z.string(),
  transferability: z.string(),
  assignment_of_proceeds: z.string(),
  revolving: z.string(),
  revolving_mode: z.string().nullable().optional(),
  red_clause: z.string(),
  green_clause: z.string(),
  back_to_back: z.string(),
  documentation_basis: z.string(),
  partial_shipments: z.string(),
  transshipment: z.string(),
  latest_shipment_date: z.string().nullable().optional(),
  expiry_date: z.string().nullable().optional(),
  expiry_place: z.string().nullable().optional(),
  presentation_period_days: z.number().nullable().optional(),
  tenor_kind: z.string(),
  tenor_days: z.number().nullable().optional(),
  tolerance_min_pct: z.number().nullable().optional(),
  tolerance_max_pct: z.number().nullable().optional(),
  reimbursement_present: z.string(),
}).passthrough();
export type LcClassificationAttributes = z.infer<typeof LcClassificationAttributesSchema>;

export const LcClassificationSchema = z.object({
  format_family: z.string(),
  format_variant: z.string(),
  embedded_variant: z.string().nullable().optional(),
  instrument_type: z.string(),
  workflow_orientation: z.string(),
  applicable_rules: z.string(),
  attributes: LcClassificationAttributesSchema,
  required_documents: z.array(LcRequiredDocumentSchema),
  requirement_conditions: z.array(z.string()).optional(),
  unmapped_requirements: z.array(z.string()).optional(),
}).passthrough();
export type LcClassification = z.infer<typeof LcClassificationSchema>;

export const LcStructuredPayloadSchema = z.object({
  lc_classification: LcClassificationSchema.optional().nullable(),
  documents_required: z.union([z.array(z.string()), z.string()]).optional(),
  required_document_types: z.array(z.string()).optional(),
  required_documents_detailed: z.array(LcRequiredDocumentSchema).optional(),
  requirement_conditions: z.array(z.string()).optional(),
  unmapped_requirements: z.array(z.string()).optional(),
  additional_conditions: z.union([z.array(z.string()), z.string()]).optional(),
  documents_structured: z.array(z.record(z.unknown())).optional(),
}).passthrough();
export type LcStructuredPayload = z.infer<typeof LcStructuredPayloadSchema>;

export type StructuredResult = {
  version: 'structured_result_v1';
  result_contract_version?: 'examination_result_v1';
  processing_summary: ProcessingSummary;
  processing_summary_v2?: ProcessingSummaryV2;
  document_extraction_v1?: DocumentExtractionV1;
  requirements_graph_v1?: RequirementsGraphV1;
  resolution_queue_v1?: ResolutionQueueV1;
  fact_resolution_v1?: FactResolutionV1;
  issue_provenance_v1?: IssueProvenanceV1;
  documents: StructuredResultDocument[];
  issues: StructuredResultIssue[];
  analytics: StructuredResultAnalytics;
  timeline: StructuredTimelineEntry[];
  extracted_documents?: Record<string, unknown>;
  lc_structured?: LcStructuredPayload;
  _extraction_core_v1?: Record<string, unknown>;
  _extraction_diagnostics?: Record<string, unknown>;
  ai_validation?: AiValidationSummary;
  validation_contract_v1?: ValidationContractV1;
  submission_eligibility?: SubmissionEligibility;
  raw_submission_eligibility?: SubmissionEligibility;
  effective_submission_eligibility?: SubmissionEligibility;
  workflow_stage?: WorkflowStageInfo;
  workflowStage?: WorkflowStageInfo;
  bank_verdict?: BankVerdict;
  bank_profile?: BankProfile;
  amendments_available?: AmendmentsAvailable;
  [key: string]: unknown;
};

type StructuredResultInput = {
  version: 'structured_result_v1';
  result_contract_version?: 'examination_result_v1';
  processing_summary: z.input<typeof ProcessingSummarySchema>;
  processing_summary_v2?: z.input<typeof ProcessingSummaryV2Schema>;
  document_extraction_v1?: z.input<typeof DocumentExtractionV1Schema>;
  requirements_graph_v1?: z.input<typeof RequirementsGraphV1Schema>;
  resolution_queue_v1?: z.input<typeof ResolutionQueueV1Schema>;
  fact_resolution_v1?: z.input<typeof FactResolutionV1Schema>;
  issue_provenance_v1?: z.input<typeof IssueProvenanceV1Schema>;
  documents: z.input<typeof StructuredResultDocumentSchema>[];
  issues: z.input<typeof StructuredResultIssueSchema>[];
  analytics: z.input<typeof StructuredResultAnalyticsSchema>;
  timeline: z.input<typeof StructuredTimelineEntrySchema>[];
  extracted_documents?: Record<string, unknown>;
  lc_structured?: z.input<typeof LcStructuredPayloadSchema>;
  _extraction_core_v1?: Record<string, unknown>;
  _extraction_diagnostics?: Record<string, unknown>;
  ai_validation?: z.input<typeof AiValidationSummarySchema>;
  validation_contract_v1?: z.input<typeof ValidationContractV1Schema>;
  submission_eligibility?: z.input<typeof SubmissionEligibilitySchema>;
  raw_submission_eligibility?: z.input<typeof SubmissionEligibilitySchema>;
  effective_submission_eligibility?: z.input<typeof SubmissionEligibilitySchema>;
  workflow_stage?: z.input<typeof WorkflowStageInfoSchema>;
  workflowStage?: z.input<typeof WorkflowStageInfoSchema>;
  bank_verdict?: z.input<typeof BankVerdictSchema>;
  bank_profile?: z.input<typeof BankProfileSchema>;
  amendments_available?: z.input<typeof AmendmentsAvailableSchema>;
  [key: string]: unknown;
};

export const StructuredResultSchema = z.object({
  version: z.literal('structured_result_v1'),
  result_contract_version: z.literal('examination_result_v1').optional(),
  processing_summary: ProcessingSummarySchema,
  processing_summary_v2: ProcessingSummaryV2Schema.optional(),
  document_extraction_v1: DocumentExtractionV1Schema.optional(),
  requirements_graph_v1: RequirementsGraphV1Schema.optional(),
  resolution_queue_v1: ResolutionQueueV1Schema.optional(),
  fact_resolution_v1: FactResolutionV1Schema.optional(),
  issue_provenance_v1: IssueProvenanceV1Schema.optional(),
  documents: z.array(StructuredResultDocumentSchema),
  issues: z.array(StructuredResultIssueSchema),
  analytics: StructuredResultAnalyticsSchema,
  timeline: z.array(StructuredTimelineEntrySchema),
  extracted_documents: z.record(z.unknown()).optional(),
  lc_structured: LcStructuredPayloadSchema.optional(),
  _extraction_core_v1: z.record(z.unknown()).optional(),
  _extraction_diagnostics: z.record(z.unknown()).optional(),
  ai_validation: AiValidationSummarySchema.optional(),
  validation_contract_v1: ValidationContractV1Schema.optional(),
  submission_eligibility: SubmissionEligibilitySchema.optional(),
  raw_submission_eligibility: SubmissionEligibilitySchema.optional(),
  effective_submission_eligibility: SubmissionEligibilitySchema.optional(),
  workflow_stage: WorkflowStageInfoSchema.optional(),
  workflowStage: WorkflowStageInfoSchema.optional(),
  bank_verdict: BankVerdictSchema.optional(),
  bank_profile: BankProfileSchema.optional(),
  amendments_available: AmendmentsAvailableSchema.optional(),
}).passthrough() as z.ZodType<
  StructuredResult,
  z.ZodTypeDef,
  StructuredResultInput
>; // Allow extra fields for backward compatibility
export const StructuredProcessingSummarySchema = ProcessingSummarySchema;
export type StructuredProcessingSummary = ProcessingSummary;
export const PaginationParamsSchema = z.object({
  page: z.number().int().positive().default(1),
  limit: z.number().int().positive().max(100).default(20),
  sort_by: z.string().optional(),
  sort_order: z.enum(['asc', 'desc']).default('desc'),
});
export type PaginationParams = z.infer<typeof PaginationParamsSchema>;

export const PaginationMetaSchema = z.object({
  page: z.number(),
  limit: z.number(),
  total: z.number(),
  total_pages: z.number(),
  has_next: z.boolean(),
  has_prev: z.boolean(),
});
export type PaginationMeta = z.infer<typeof PaginationMetaSchema>;

export const PaginatedResponseSchema = <T extends z.ZodType>(itemSchema: T) =>
  z.object({
    items: z.array(itemSchema),
    meta: PaginationMetaSchema,
  });

// ============================================================================
// Exporter Bank Directory Types
// ============================================================================

export const ExporterBankDirectoryItemSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  legal_name: z.string().optional(),
  country: z.string().optional(),
  regulator_id: z.string().optional(),
  active_user_count: z.number().nonnegative(),
});
export type ExporterBankDirectoryItem = z.infer<typeof ExporterBankDirectoryItemSchema>;

export const ExporterBankDirectoryResponseSchema = z.object({
  items: z.array(ExporterBankDirectoryItemSchema),
  total: z.number().nonnegative(),
});
export type ExporterBankDirectoryResponse = z.infer<typeof ExporterBankDirectoryResponseSchema>;

// ============================================================================
// API Response Wrappers
// ============================================================================

export const SuccessResponseSchema = <T extends z.ZodType>(dataSchema: T) =>
  z.object({
    success: z.literal(true),
    data: dataSchema,
    timestamp: z.string().datetime(),
  });

export const ErrorResponseSchema = z.object({
  success: z.literal(false),
  error: ApiErrorSchema,
  timestamp: z.string().datetime(),
});

// ============================================================================
// LCopilot Issue Card Schema
// ============================================================================

export const ToleranceAppliedSchema = z.object({
  tolerance_percent: z.number(),
  source: z.string(),
  explicit: z.boolean(),
});
export type ToleranceApplied = z.infer<typeof ToleranceAppliedSchema>;

export const WorkflowLaneSchema = z.enum([
  'documentary_review',
  'compliance_review',
  'manual_review',
]);
export type WorkflowLane = z.infer<typeof WorkflowLaneSchema>;

export const IssueCardSchema = z.object({
  id: z.string(),
  rule: z.string().optional(),
  title: z.string(),
  description: z.string(),
  severity: z.string(),
  priority: z.string().optional(),
  documentName: z.string().optional(),
  documentType: z.string().optional(),
  documents: z.array(z.string()).optional(),
  expected: z.string().optional(),
  actual: z.string().optional(),
  suggestion: z.string().optional(),
  field: z.string().optional(),
  source_field: z.string().optional(),
  target_field: z.string().optional(),
  dependency_fields: z.array(z.string()).optional(),
  provisional: z.boolean().optional(),
  provisional_reason: z.string().optional(),
  ucpReference: z.string().optional().nullable(),
  ucpDescription: z.string().optional().nullable(),
  ruleset_domain: z.string().optional(),
  auto_generated: z.boolean().optional(),
  isbpReference: z.string().optional().nullable(),
  isbpDescription: z.string().optional().nullable(),
  tolerance_applied: ToleranceAppliedSchema.optional(),
  extraction_confidence: z.number().optional(),
  amendment_available: z.boolean().optional(),
  bucket: z.string().optional(),
  severity_display: z.string().optional(),
  lc_basis: z.string().optional(),
  found: z.string().optional(),
  examiner_note: z.string().optional(),
  fix_owner: z.string().optional(),
  next_action: z.string().optional(),
  confidence: z.number().optional(),
  remediation_owner: z.string().optional(),
  workflow_lane: WorkflowLaneSchema.optional(),
  lc_clause: z.string().optional(),
  impact: z.string().optional(),
  source_layer: z.string().optional(),
  field_name: z.string().optional(),
  requirement_source: z.string().optional(),
  requirement_kind: z.string().optional(),
  requirement_text: z.string().optional(),
});
export type IssueCard = z.infer<typeof IssueCardSchema>;

export const ReferenceIssueSchema = z.object({
  rule: z.string().optional(),
  title: z.string().optional(),
  severity: z.string().optional(),
  message: z.string().optional(),
  article: z.string().optional(),
  ruleset_domain: z.string().optional(),
});
export type ReferenceIssue = z.infer<typeof ReferenceIssueSchema>;

// ============================================================================
// AI Enrichment Schema
// ============================================================================

export const RuleReferenceSchema = z.object({
  rule_code: z.string(),
  title: z.string().optional(),
});
export type RuleReference = z.infer<typeof RuleReferenceSchema>;

export const AIEnrichmentPayloadSchema = z.object({
  summary: z.string().optional(),
  suggestions: z.array(z.string()).optional(),
  confidence: z.string().optional(),
  rule_references: z.union([
    z.array(z.string()),
    z.array(RuleReferenceSchema),
  ]).optional(),
  fallback_used: z.boolean().optional(),
});
export type AIEnrichmentPayload = z.infer<typeof AIEnrichmentPayloadSchema>;

// ============================================================================
// V2 Validation Pipeline Schemas
// ============================================================================

export const GateResultSchema = z.object({
  status: z.enum(['passed', 'blocked', 'warning']),
  can_proceed: z.boolean(),
  block_reason: z.string().optional().nullable(),
  completeness: z.number(),
  critical_completeness: z.number(),
  missing_critical: z.array(z.string()),
  missing_required: z.array(z.string()).optional(),
  blocking_issues: z.array(z.record(z.unknown())).optional(),
  warning_issues: z.array(z.record(z.unknown())).optional(),
});
export type GateResult = z.infer<typeof GateResultSchema>;

export const ExtractionSummarySchema = z.object({
  completeness: z.number(),
  critical_completeness: z.number(),
  missing_critical: z.array(z.string()),
  missing_required: z.array(z.string()).optional(),
  total_fields: z.number().optional(),
  extracted_fields: z.number().optional(),
});
export type ExtractionSummary = z.infer<typeof ExtractionSummarySchema>;

export const LCBaselineSchema = z.object({
  lc_number: z.string().optional().nullable(),
  lc_type: z.string().optional().nullable(),
  applicant: z.string().optional().nullable(),
  beneficiary: z.string().optional().nullable(),
  issuing_bank: z.string().optional().nullable(),
  advising_bank: z.string().optional().nullable(),
  amount: z.string().optional().nullable(),
  currency: z.string().optional().nullable(),
  expiry_date: z.string().optional().nullable(),
  issue_date: z.string().optional().nullable(),
  latest_shipment: z.string().optional().nullable(),
  port_of_loading: z.string().optional().nullable(),
  port_of_discharge: z.string().optional().nullable(),
  goods_description: z.string().optional().nullable(),
  incoterm: z.string().optional().nullable(),
  extraction_completeness: z.number(),
  critical_completeness: z.number(),
});
export type LCBaseline = z.infer<typeof LCBaselineSchema>;

// ============================================================================
// Sanctions Screening Schemas
// ============================================================================

export const SanctionsScreeningIssueSchema = z.object({
  party: z.string().nullable(),
  type: z.string().nullable(),
  status: z.enum(['match', 'potential_match', 'clear']),
  score: z.number().nullable(),
});
export type SanctionsScreeningIssue = z.infer<typeof SanctionsScreeningIssueSchema>;

export const SanctionsScreeningSummarySchema = z.object({
  screened: z.boolean(),
  parties_screened: z.number(),
  matches: z.number(),
  potential_matches: z.number(),
  clear: z.number(),
  should_block: z.boolean(),
  screened_at: z.string(),
  issues: z.array(SanctionsScreeningIssueSchema),
  error: z.string().optional(),
});
export type SanctionsScreeningSummary = z.infer<typeof SanctionsScreeningSummarySchema>;

export const DocumentRequirementStatusSchema = z.enum(['matched', 'partial', 'missing']);
export type DocumentRequirementStatus = z.infer<typeof DocumentRequirementStatusSchema>;

export const DocumentReviewStateSchema = z.enum(['ready', 'needs_review', 'blocked']);
export type DocumentReviewState = z.infer<typeof DocumentReviewStateSchema>;

// ============================================================================
// Validation Document Schema (Frontend-specific normalization)
// ============================================================================

export const ValidationDocumentSchema = z.object({
  id: z.string(),
  documentId: z.string(),
  name: z.string(),
  filename: z.string(),
  type: z.string(),
  typeKey: z.string().optional(),
  extractionStatus: z.string(),
  extractionLane: z.string().optional(),
  status: z.enum(['success', 'warning', 'error']),
  issuesCount: z.number(),
  extractedFields: z.record(z.unknown()),
  fieldDetails: z.record(ExtractionFieldDetailSchema).optional(),
  missingRequiredFields: z.array(z.string()).optional(),
  requiredFieldsFound: z.number().optional(),
  requiredFieldsTotal: z.number().optional(),
  reviewRequired: z.boolean().optional(),
  reviewReasons: z.array(z.string()).optional(),
  criticalFieldStates: z.record(z.string()).optional(),
  extractionResolution: z.object({
    required: z.boolean(),
    unresolvedCount: z.number().nonnegative(),
    summary: z.string(),
    fields: z.array(
      z.object({
        fieldName: z.string(),
        label: z.string(),
        verification: z.string().optional(),
      })
    ),
  }).optional(),
  requirementStatus: DocumentRequirementStatusSchema.optional(),
  reviewState: DocumentReviewStateSchema.optional(),
});
export type ValidationDocument = z.infer<typeof ValidationDocumentSchema>;

export const ContractWarningSchema = z.object({
  field: z.string(),
  message: z.string(),
  severity: z.enum(['error', 'warning', 'info']),
  source: z.string(),
  suggestion: z.string().nullable().optional(),
});
export type ContractWarning = z.infer<typeof ContractWarningSchema>;

export const ContractValidationSchema = z.object({
  valid: z.boolean(),
  error_count: z.number(),
  warning_count: z.number(),
  info_count: z.number(),
});
export type ContractValidation = z.infer<typeof ContractValidationSchema>;

// ============================================================================
// Full Validation Results Schema (LCopilot Response)
// ============================================================================

export type ValidationResults = {
  jobId: string;
  job_id?: string;
  validation_session_id?: string;
  summary: ProcessingSummary;
  documents: ValidationDocument[];
  issues: IssueCard[];
  provisional_issues?: IssueCard[];
  analytics: StructuredResultAnalytics;
  timeline: StructuredTimelineEntry[];
  structured_result: StructuredResult;
  lc_structured?: LcStructuredPayload | null;
  ai_enrichment?: AIEnrichmentPayload | null;
  telemetry?: Record<string, unknown>;
  reference_issues?: ReferenceIssue[];
  validationBlocked?: boolean;
  validationStatus?: string;
  gateResult?: GateResult | null;
  extractionSummary?: ExtractionSummary | null;
  lcBaseline?: LCBaseline | null;
  complianceLevel?: string;
  complianceCapReason?: string | null;
  sanctionsScreening?: SanctionsScreeningSummary | null;
  sanctionsBlocked?: boolean;
  sanctionsBlockReason?: string | null;
  validation_contract_v1?: ValidationContractV1 | null;
  submission_eligibility?: SubmissionEligibility | null;
  raw_submission_eligibility?: SubmissionEligibility | null;
  effective_submission_eligibility?: SubmissionEligibility | null;
  bank_verdict?: BankVerdict | null;
  ruleset_verdict?: string | null;
  final_verdict?: 'pass' | 'review' | 'reject' | null;
  submission_can_submit?: boolean;
  submission_reasons?: string[];
  contractWarnings?: ContractWarning[];
  contractValidation?: ContractValidation | null;
};

type ValidationResultsInput = {
  jobId: string;
  job_id?: string;
  validation_session_id?: string;
  summary: z.input<typeof ProcessingSummarySchema>;
  documents: z.input<typeof ValidationDocumentSchema>[];
  issues: z.input<typeof IssueCardSchema>[];
  provisional_issues?: z.input<typeof IssueCardSchema>[];
  analytics: z.input<typeof StructuredResultAnalyticsSchema>;
  timeline: z.input<typeof StructuredTimelineEntrySchema>[];
  structured_result: StructuredResultInput;
  lc_structured?: z.input<typeof LcStructuredPayloadSchema> | null;
  ai_enrichment?: z.input<typeof AIEnrichmentPayloadSchema> | null;
  telemetry?: Record<string, unknown>;
  reference_issues?: z.input<typeof ReferenceIssueSchema>[];
  validationBlocked?: boolean;
  validationStatus?: string;
  gateResult?: z.input<typeof GateResultSchema> | null;
  extractionSummary?: z.input<typeof ExtractionSummarySchema> | null;
  lcBaseline?: z.input<typeof LCBaselineSchema> | null;
  complianceLevel?: string;
  complianceCapReason?: string | null;
  sanctionsScreening?: z.input<typeof SanctionsScreeningSummarySchema> | null;
  sanctionsBlocked?: boolean;
  sanctionsBlockReason?: string | null;
  validation_contract_v1?: z.input<typeof ValidationContractV1Schema> | null;
  submission_eligibility?: z.input<typeof SubmissionEligibilitySchema> | null;
  raw_submission_eligibility?: z.input<typeof SubmissionEligibilitySchema> | null;
  effective_submission_eligibility?: z.input<typeof SubmissionEligibilitySchema> | null;
  bank_verdict?: z.input<typeof BankVerdictSchema> | null;
  ruleset_verdict?: string | null;
  final_verdict?: 'pass' | 'review' | 'reject' | null;
  submission_can_submit?: boolean;
  submission_reasons?: string[];
  contractWarnings?: z.input<typeof ContractWarningSchema>[];
  contractValidation?: z.input<typeof ContractValidationSchema> | null;
};

export const ValidationResultsSchema = z.object({
  jobId: z.string(),
  job_id: z.string().optional(),
  validation_session_id: z.string().optional(),
  summary: ProcessingSummarySchema,
  documents: z.array(ValidationDocumentSchema),
  issues: z.array(IssueCardSchema),
  provisional_issues: z.array(IssueCardSchema).optional(),
  analytics: StructuredResultAnalyticsSchema,
  timeline: z.array(StructuredTimelineEntrySchema),
  structured_result: StructuredResultSchema,
  lc_structured: LcStructuredPayloadSchema.optional().nullable(),
  ai_enrichment: AIEnrichmentPayloadSchema.optional().nullable(),
  telemetry: z.record(z.unknown()).optional(),
  reference_issues: z.array(ReferenceIssueSchema).optional(),
  
  // V2 Validation Pipeline additions
  validationBlocked: z.boolean().optional(),
  validationStatus: z.string().optional(),
  gateResult: GateResultSchema.optional().nullable(),
  extractionSummary: ExtractionSummarySchema.optional().nullable(),
  lcBaseline: LCBaselineSchema.optional().nullable(),
  complianceLevel: z.string().optional(),
  complianceCapReason: z.string().optional().nullable(),
  
  // Sanctions Screening additions
  sanctionsScreening: SanctionsScreeningSummarySchema.optional().nullable(),
  sanctionsBlocked: z.boolean().optional(),
  sanctionsBlockReason: z.string().optional().nullable(),
  validation_contract_v1: ValidationContractV1Schema.optional().nullable(),
  submission_eligibility: SubmissionEligibilitySchema.optional().nullable(),
  raw_submission_eligibility: SubmissionEligibilitySchema.optional().nullable(),
  effective_submission_eligibility: SubmissionEligibilitySchema.optional().nullable(),
  bank_verdict: BankVerdictSchema.optional().nullable(),
  ruleset_verdict: z.string().optional().nullable(),
  final_verdict: z.enum(['pass', 'review', 'reject']).optional().nullable(),
  submission_can_submit: z.boolean().optional(),
  submission_reasons: z.array(z.string()).optional(),
  contractWarnings: z.array(ContractWarningSchema).optional(),
  contractValidation: ContractValidationSchema.optional().nullable(),
}) as z.ZodType<
  ValidationResults,
  z.ZodTypeDef,
  ValidationResultsInput
>;

// ============================================================================
// API Response Validation Helper
// ============================================================================

/**
 * Safely parse and validate an API response against a Zod schema.
 * Returns the parsed data or throws a detailed error.
 */
export function validateApiResponse<T>(
  schema: z.ZodType<T>,
  data: unknown,
  context?: string
): T {
  const result = schema.safeParse(data);
  if (!result.success) {
    const errorMessage = result.error.errors
      .map((e) => `${e.path.join('.')}: ${e.message}`)
      .join('; ');
    throw new Error(
      `API Response Validation Failed${context ? ` (${context})` : ''}: ${errorMessage}`
    );
  }
  return result.data;
}

/**
 * Safely parse API response, returning null on failure instead of throwing.
 * Logs validation errors to console in development.
 */
export function safeValidateApiResponse<T>(
  schema: z.ZodType<T>,
  data: unknown,
  context?: string
): T | null {
  const result = schema.safeParse(data);
  if (!result.success) {
    if (process.env.NODE_ENV === 'development') {
      console.warn(
        `API Response Validation Warning${context ? ` (${context})` : ''}:`,
        result.error.errors
      );
    }
    return null;
  }
  return result.data;
}

// ============================================================================
// Onboarding — 3-question wizard (Day 1 backend, Day 2 frontend)
// ============================================================================
// Mirrors apps/api/app/schemas/onboarding.py::OnboardingCompletePayload and
// apps/api/app/models/company.py::BusinessActivity / BusinessTier. Keep in
// lockstep — drift between Zod and Pydantic bites at runtime when
// parseOnboarding-style paths start firing.

export const BusinessActivitySchema = z.enum(['exporter', 'importer', 'agent', 'services']);
export type BusinessActivity = z.infer<typeof BusinessActivitySchema>;

export const BusinessTierSchema = z.enum(['solo', 'sme', 'enterprise']);
export type BusinessTier = z.infer<typeof BusinessTierSchema>;

export const OnboardingCompletePayloadSchema = z.object({
  activities: z.array(BusinessActivitySchema).min(1),
  country: z.string().length(2).regex(/^[A-Z]{2}$/, 'ISO 3166-1 alpha-2, uppercase'),
  tier: BusinessTierSchema,
  company_name: z.string().optional(),
});
export type OnboardingCompletePayload = z.infer<typeof OnboardingCompletePayloadSchema>;

export const OnboardingRequirementsSchema = z.object({
  basic: z.array(z.string()).default([]),
  legal: z.array(z.string()).default([]),
  docs: z.array(z.string()).default([]),
});
export type OnboardingRequirements = z.infer<typeof OnboardingRequirementsSchema>;

export const OnboardingStatusSchema = z.object({
  user_id: z.string(),
  role: z.string().nullable(),
  company_id: z.string().nullable().optional(),
  completed: z.boolean().default(false),
  step: z.string().nullable().optional(),
  status: z.string().nullable().optional(),
  kyc_status: z.string().nullable().optional(),
  required: OnboardingRequirementsSchema.default({ basic: [], legal: [], docs: [] }),
  details: z.record(z.unknown()).default({}),
});
export type OnboardingStatus = z.infer<typeof OnboardingStatusSchema>;


// ============================================================================
// Schema Collections for Export
// ============================================================================

export type StructuredResultPayload = StructuredResult;

export const schemas: Readonly<Record<string, z.ZodTypeAny>> = {
  // Health
  HealthResponse: HealthResponseSchema,
  ServiceStatus: ServiceStatusSchema,
  
  // Errors
  ApiError: ApiErrorSchema,
  ValidationError: ValidationErrorSchema,
  
  // Auth
  AuthToken: AuthTokenSchema,
  UserProfile: UserProfileSchema,
  
  // Files
  FileUploadRequest: FileUploadRequestSchema,
  FileUploadResponse: FileUploadResponseSchema,
  FileInfo: FileInfoSchema,
  
  // OCR
  OcrJobRequest: OcrJobRequestSchema,
  OcrJobResponse: OcrJobResponseSchema,
  OcrResult: OcrResultSchema,
  
  // Reports
  ReportRequest: ReportRequestSchema,
  ReportJob: ReportJobSchema,
  
  // Pagination
  PaginationParams: PaginationParamsSchema,
  PaginationMeta: PaginationMetaSchema,
  ExporterBankDirectoryItem: ExporterBankDirectoryItemSchema,
  ExporterBankDirectoryResponse: ExporterBankDirectoryResponseSchema,

  // Structured validation payload
  StructuredProcessingSummary: StructuredProcessingSummarySchema,
  ProcessingSummaryV2: ProcessingSummaryV2Schema,
  DocumentFact: DocumentFactSchema,
  DocumentFactSet: DocumentFactSetSchema,
  ResolutionQueueItem: ResolutionQueueItemSchema,
  ResolutionQueueSummary: ResolutionQueueSummarySchema,
  ResolutionQueueV1: ResolutionQueueV1Schema,
  DocumentExtractionV1: DocumentExtractionV1Schema,
  IssueProvenanceV1: IssueProvenanceV1Schema,
  StructuredResultDocument: StructuredResultDocumentSchema,
  StructuredResultIssue: StructuredResultIssueSchema,
  StructuredResultDocumentRiskEntry: DocumentRiskEntrySchema,
  StructuredResultAnalytics: StructuredResultAnalyticsSchema,
  StructuredResultTimelineEntry: TimelineEntrySchema,
  AiValidationLayer: AiValidationLayerSchema,
  AiValidationLayers: AiValidationLayersSchema,
  AiValidationSummary: AiValidationSummarySchema,
  ValidationContractV1: ValidationContractV1Schema,
  SubmissionEligibility: SubmissionEligibilitySchema,
  BankVerdict: BankVerdictSchema,
  BankProfile: BankProfileSchema,
  AmendmentFieldChange: AmendmentFieldChangeSchema,
  Amendment: AmendmentSchema,
  AmendmentsAvailable: AmendmentsAvailableSchema,
  LcRequiredDocument: LcRequiredDocumentSchema,
  LcClassificationAttributes: LcClassificationAttributesSchema,
  LcClassification: LcClassificationSchema,
  LcStructuredPayload: LcStructuredPayloadSchema,
  StructuredResult: StructuredResultSchema,
  
  // LCopilot-specific schemas
  IssueCard: IssueCardSchema,
  ReferenceIssue: ReferenceIssueSchema,
  AIEnrichmentPayload: AIEnrichmentPayloadSchema,
  ToleranceApplied: ToleranceAppliedSchema,
  WorkflowLane: WorkflowLaneSchema,
  
  // V2 Validation Pipeline
  GateResult: GateResultSchema,
  ExtractionSummary: ExtractionSummarySchema,
  LCBaseline: LCBaselineSchema,
  
  // Sanctions Screening
  SanctionsScreeningIssue: SanctionsScreeningIssueSchema,
  SanctionsScreeningSummary: SanctionsScreeningSummarySchema,
  
  // Full validation results
  ValidationDocument: ValidationDocumentSchema,
  DocumentRequirementStatus: DocumentRequirementStatusSchema,
  DocumentReviewState: DocumentReviewStateSchema,
  ContractWarning: ContractWarningSchema,
  ContractValidation: ContractValidationSchema,
  ValidationResults: ValidationResultsSchema,

  // Onboarding wizard (3-question model — activities × country × tier)
  BusinessActivity: BusinessActivitySchema,
  BusinessTier: BusinessTierSchema,
  OnboardingCompletePayload: OnboardingCompletePayloadSchema,
  OnboardingRequirements: OnboardingRequirementsSchema,
  OnboardingStatus: OnboardingStatusSchema,
} as const;
